import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const brandNames = [
  { name: 'BMW', icon: 'ri-car-line' },
  { name: 'Mercedes-Benz', icon: 'ri-car-line' },
  { name: 'Audi', icon: 'ri-car-line' },
  { name: 'Volkswagen', icon: 'ri-car-line' },
];

export default function Hero() {
  const { t } = useTranslation();
  const marqueeRef = useRef<HTMLDivElement>(null);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="inicio" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/0c7bdc23c0509608cd59066307fba6e0/03923e8d56022426884d46a6527cc705.png"
          alt="ZS Cars Import Hero"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-[#050505]/70 to-[#050505]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-transparent" />
      </div>

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-purple-400/60"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-24 sm:pt-28 flex-1 flex items-center">
        <div className="w-full max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 glass-card px-3 sm:px-4 py-2 rounded-full mb-6 sm:mb-8 animate-fade-up max-w-full overflow-hidden">
            <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse-glow flex-shrink-0" />
            <span className="text-purple-300 text-xs tracking-wider sm:tracking-widest uppercase font-medium truncate">
              {t('hero.badge')}
            </span>
          </div>

          {/* Main Title */}
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl xl:text-[7rem] font-normal leading-none mb-5 sm:mb-6 animate-fade-up" style={{ animationDelay: '0.2s', letterSpacing: '0.03em' }}>
            <span className="gradient-text-purple glow-purple-text">{t('hero.title1')}</span>
            <br />
            <span className="text-white">{t('hero.title2')} </span>
            <span className="gradient-text-purple glow-purple-text">{t('hero.title3')}</span>
            <span className="text-white"> {t('hero.title4')}</span>
            <br />
            <span className="text-white">{t('hero.title5')}</span>
          </h1>

          {/* Subtitle */}
          <p className="font-body text-white/60 text-base sm:text-lg lg:text-xl font-light leading-relaxed max-w-xl mb-8 sm:mb-10 animate-fade-up" style={{ animationDelay: '0.4s', letterSpacing: '0.02em' }}>
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-10 sm:mb-16 animate-fade-up" style={{ animationDelay: '0.6s' }}>
            <button
              onClick={() => handleScroll('#catalogo')}
              className="gradient-purple text-white font-semibold tracking-wider uppercase px-8 py-4 rounded-full glow-purple hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-car-line mr-2" />
              {t('hero.cta.catalog')}
            </button>
            <button
              onClick={() => handleScroll('#contacto')}
              className="glass-card text-white font-semibold tracking-wider uppercase px-8 py-4 rounded-full hover:border-purple-500/50 hover:bg-white/5 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm border border-white/20"
            >
              <i className="ri-message-3-line mr-2" />
              {t('hero.cta.quote')}
            </button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-2 sm:gap-4 animate-fade-up" style={{ animationDelay: '0.8s' }}>
            {(['hero.badge.certified', 'hero.badge.approved', 'hero.badge.guaranteed', 'hero.badge.insured'] as const).map((key) => (
              <div key={key} className="flex items-center gap-2 glass-card px-3 py-2 rounded-lg">
                <i className="ri-shield-check-line text-purple-400 text-sm" />
                <span className="text-white/70 text-xs tracking-wider">{t(key)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Brands Marquee Strip */}
      <div className="relative z-10 w-full mt-auto animate-fade-up" style={{ animationDelay: '1s' }}>
        <div className="w-full h-px bg-gradient-to-r from-transparent via-purple-500/60 to-transparent" />

        <div className="relative bg-[#050505]/70 backdrop-blur-md py-4 sm:py-5 overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-24 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-24 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none" />

          <div
            ref={marqueeRef}
            className="flex items-center gap-10 sm:gap-16"
            style={{
              animation: 'marquee 18s linear infinite',
              width: 'max-content',
            }}
          >
            {[...brandNames, ...brandNames, ...brandNames].map((brand, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 sm:px-6 group cursor-default"
              >
                <div
                  className="w-2 h-2 rounded-full bg-purple-500/70 group-hover:bg-purple-400 transition-colors duration-300 flex-shrink-0"
                  style={{ boxShadow: '0 0 8px rgba(168,85,247,0.8)' }}
                />
                <span
                  className="font-label text-xs sm:text-sm tracking-widest uppercase transition-all duration-300"
                  style={{ color: 'rgba(255,255,255,0.45)', textShadow: 'none', transition: 'color 0.3s, text-shadow 0.3s' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLSpanElement).style.color = 'rgba(255,255,255,0.95)';
                    (e.currentTarget as HTMLSpanElement).style.textShadow = '0 0 20px rgba(168,85,247,0.9), 0 0 40px rgba(168,85,247,0.5)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLSpanElement).style.color = 'rgba(255,255,255,0.45)';
                    (e.currentTarget as HTMLSpanElement).style.textShadow = 'none';
                  }}
                >
                  {brand.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </section>
  );
}